'use client';

import React, { useEffect, useState, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { fetchBrandRevenue } from '@/lib/supabase/dashboardService';
import { createClient } from '@/lib/supabase/client';

const COLORS = ['var(--primary)', 'var(--accent)'];

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: { units: number } }>;
}

function CustomTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0];
  return (
    <div className="bg-card border border-border rounded-xl shadow-dropdown px-3 py-2 text-xs">
      <p className="font-600 text-foreground">{d.name}</p>
      <p className="text-muted-foreground">₹{(d.value / 100000).toFixed(2)}L revenue</p>
      <p className="text-muted-foreground">{d.payload.units.toLocaleString('en-IN')} units</p>
    </div>
  );
}

export default function BrandRevenueChart() {
  const [brandData, setBrandData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(() => {
    fetchBrandRevenue(30).then((result) => {
      if (result && result.length) setBrandData(result);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    loadData();

    const supabase = createClient();
    const channel = supabase
      .channel('brand-revenue-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, loadData)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadData]);

  if (loading) return <div className="h-[200px] bg-muted/30 rounded-lg animate-pulse" />;
  if (!brandData.length) return <div className="h-[200px] flex items-center justify-center text-xs text-muted-foreground">No brand data available</div>;

  const total = brandData.reduce((s, d) => s + d.value, 0);

  return (
    <div>
      <ResponsiveContainer width="100%" height={160}>
        <PieChart>
          <Pie data={brandData} cx="50%" cy="50%" innerRadius={48} outerRadius={72} paddingAngle={3} dataKey="value">
            {brandData.map((_, i) => (
              <Cell key={`brand-cell-${i}`} fill={COLORS[i % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex flex-col gap-2 mt-2">
        {brandData.map((d, i) => (
          <div key={`brand-legend-${d.name}`} className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i] }} />
              <span className="font-500 text-foreground">{d.name}</span>
            </div>
            <div className="text-right">
              <span className="font-mono-nums font-600 text-foreground">₹{(d.value / 100000).toFixed(1)}L</span>
              <span className="text-muted-foreground ml-1">({total > 0 ? ((d.value / total) * 100).toFixed(0) : 0}%)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}